import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  viewChildren,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AccessibleClickDirective, ImageFallbackDirective, SuperscriptPipe } from '@shared';
import Swiper from 'swiper';
import { ICarouselSlide } from '../../interfaces/carousel-slide.interface';

@Component({
  selector: 'lib-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    ImageFallbackDirective,
    AccessibleClickDirective,
    SuperscriptPipe,
  ],
})
export class CarouselComponent {
  private readonly destroyRef = inject(DestroyRef);

  slides = input.required<ICarouselSlide[]>();
  displayAutoPlay = input(true);
  displayPagination = input(true);
  textTemplate = input<TemplateRef<unknown>>();
  objectFit = input<'contain' | 'cover' | 'fill'>();
  autoHeight = input<boolean>(false);
  overlayGradient = input<'left' | 'center' | 'none'>('none');
  mainAriaLabel = input<string>('Main content');
  whiteBackground = input<boolean>(false);
  isAppleBrand = input<boolean>(false);

  goToBrand = output<ICarouselSlide>();

  slideLinks = viewChildren('slideLink', { read: ElementRef<HTMLButtonElement> });

  isAutoplayEnabled = signal(true);
  activeIndex = signal(0);

  imageStyle = computed(() => {
    if (this.objectFit()) {
      return { 'object-fit': this.objectFit() };
    }
    return null;
  });
  isForLanding = computed(() => this.overlayGradient() === 'left');
  isForBrandPage = computed(() => this.overlayGradient() === 'center');

  swiperContainerRef = viewChild<ElementRef>('swiperContainer');
  swiperContentRef = viewChild.required<ElementRef<HTMLElement>>('swiperContent');
  swiperPaginationRef = viewChild<ElementRef>('swiperPagination');

  swiper: Swiper | null = null;

  constructor() {
    effect(() => {
      this.initSwiper();
    });
    this.destroyRef.onDestroy(() => {
      this.swiper?.destroy(true, true);
      this.swiper = null;
    });
  }

  clickShopProducts(slide: ICarouselSlide): void {
    this.goToBrand.emit(slide);
  }

  toggleAutoPlay(): void {
    this.isAutoplayEnabled.update((value) => !value);
    if (this.isAutoplayEnabled()) {
      this.swiper?.autoplay.start();
    } else {
      this.swiper?.autoplay.stop();
    }
  }

  private updateActiveIndex(swiper: Swiper): void {
    this.activeIndex.set(swiper?.realIndex ?? 0);
  }

  public setSlide(idx: number): void {
    this.swiper?.slideToLoop(idx);
  }

  private initSwiper(): void {
    const container = this.swiperContainerRef()?.nativeElement;
    if (!container) return;

    this.swiper?.destroy(true, true);
    this.swiper = null;

    this.swiper = new Swiper(container, {
      ...(this.slides()?.length > 1 && {
        loop: true,
        autoplay: {
          delay: 10000,
          disableOnInteraction: false,
        },
        keyboard: { enabled: false },
      }),
      slidesPerView: 1,
      pagination: {
        el: this.swiperPaginationRef()?.nativeElement,
        clickable: true,
      },
      on: {
        slideChange: (swiper: Swiper): void => {
          this.updateActiveIndex(swiper);
        },
        slideChangeTransitionEnd: (): void => {
          const content = this.swiperContentRef()?.nativeElement;
          const pagination = this.swiperPaginationRef()?.nativeElement;
          const active = document.activeElement as Element | null;

          if (!content || !pagination) return;

          if (active && (content.contains(active) || pagination.contains(active))) {
            requestAnimationFrame(() => this.focusActiveSlideLink());
          }
        },
      },
    });
  }

  private focusActiveSlideLink(): void {
    const links = this.slideLinks?.() ?? [];
    const idx = this.activeIndex?.() ?? 0;

    if (!links.length) return;

    const safeIdx = Math.max(0, Math.min(idx, links.length - 1));
    const el: HTMLElement | undefined = links[safeIdx]?.nativeElement;

    el?.focus();
  }
}
